import { useCallback, useEffect, useState } from "react";
import { Stack, Theme, Typography } from "@mui/material";
import { FileText } from "lucide-react";
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";
import { useAudioPlayback } from "@/modules/common/hooks/audio";
import {
  generateFallbackWaveform,
  generateWaveformFromUrl,
  WAVEFORM_BAR_COUNT,
  WaveformResult,
} from "@/modules/common/utils/audio.utils";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import { ActivityAttachment } from "@/modules/lead-page/dtos/index-v2";
import { DOCUMENT_MEDIA_TYPES } from "@/modules/lead-page/new/constants";

const getAttachmentFileName = (attachment: ActivityAttachment): string => {
  const lastSegment = attachment.mediaUrl.split("/").pop() || "";
  try {
    return decodeURIComponent(lastSegment) || "Attachment";
  } catch {
    return lastSegment || "Attachment";
  }
};

interface ActivityAttachmentsProps {
  attachments: ActivityAttachment[];
  theme: Theme;
  onRefresh?: () => void;
}

const ActivityAttachments = ({
  attachments,
  theme,
  onRefresh,
}: ActivityAttachmentsProps) => {
  const {
    togglePlay,
    isPlaying,
    getCurrentTimeForId,
    getDurationForId,
    isLoading: audioLoading,
    getErrorForId,
    playingId,
    loadedId,
    seekTo,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback();

  const [audioMetadataMap, setAudioMetadataMap] = useState<
    Record<string, WaveformResult>
  >({});

  const audioAttachments = attachments.filter((att) =>
    att.mediaType.startsWith("audio/"),
  );

  const documentAttachments = attachments.filter(
    (att) => DOCUMENT_MEDIA_TYPES.includes(att.mediaType) && att.presignedUrl,
  );

  useEffect(() => {
    const loadWaveforms = async () => {
      for (const attachment of audioAttachments) {
        if (attachment.presignedUrl && !audioMetadataMap[attachment.uid]) {
          const result = await generateWaveformFromUrl(
            attachment.presignedUrl,
            WAVEFORM_BAR_COUNT,
          );
          setAudioMetadataMap((prev) => ({
            ...prev,
            [attachment.uid]: result,
          }));
        }
      }
    };

    if (audioAttachments.length > 0) {
      loadWaveforms();
    }
  }, [audioAttachments.map((a) => a.uid).join(",")]);

  const toRecordingEntry = useCallback(
    (attachment: ActivityAttachment): RecordingEntry => {
      const localMetadata = audioMetadataMap[attachment.uid];
      const playbackDuration = getDurationForId(attachment.uid);

      const duration =
        attachment.metadata?.duration ??
        (playbackDuration > 0
          ? playbackDuration
          : localMetadata?.duration || 0);

      return {
        id: attachment.uid,
        audioBlob: null,
        audioUrl: attachment.presignedUrl,
        duration: duration > 0 ? duration : 0,
        waveformData:
          localMetadata?.waveformData ||
          generateFallbackWaveform(WAVEFORM_BAR_COUNT),
      };
    },
    [getDurationForId, audioMetadataMap],
  );

  if (audioAttachments.length === 0 && documentAttachments.length === 0) {
    return null;
  }

  return (
    <>
      {audioAttachments.map((attachment) => {
        const isCurrentPlaying = isPlaying(attachment.uid);
        const isCurrentLoading =
          audioLoading && playingId === null && loadedId !== attachment.uid;
        const audioErrorForId = getErrorForId(attachment.uid);

        return (
          <RecordingEntryDisplay
            isActivity={true}
            key={attachment.uid}
            recording={toRecordingEntry(attachment)}
            isPlaying={isCurrentPlaying}
            currentPlaybackTime={getCurrentTimeForId(attachment.uid)}
            onTogglePlay={() =>
              togglePlay(attachment.uid, attachment.presignedUrl)
            }
            theme={theme}
            isLoading={isCurrentLoading}
            error={audioErrorForId}
            onRefresh={onRefresh}
            onSeek={(time) => seekTo(attachment.uid, time)}
            playbackRate={playbackRate}
            onPlaybackRateChange={setPlaybackRate}
          />
        );
      })}
      {documentAttachments.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={theme.nawy.spacing.xs}
          sx={{ marginTop: theme.nawy.spacing.xs }}
        >
          {documentAttachments.map((attachment) => {
            const fileName = getAttachmentFileName(attachment);
            return (
              <Stack
                key={attachment.uid}
                component="a"
                href={attachment.presignedUrl}
                target="_blank"
                rel="noopener noreferrer"
                direction="row"
                alignItems="center"
                spacing={theme.nawy.spacing.xs}
                sx={{
                  textDecoration: "none",
                  padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
                  borderRadius: theme.nawy.radius.md,
                  border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
                  backgroundColor: theme.nawy.colors.semantic.layer.primary,
                  maxWidth: 260,
                }}
              >
                <FileText
                  size={16}
                  color={theme.nawy.colors.semantic.text.secondary}
                />
                <Typography
                  noWrap
                  title={fileName}
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {fileName}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}
    </>
  );
};

export default ActivityAttachments;
